import { addressFromPrefix, cleanList, joinName } from "../utils";

// Generic phone/email fallbacks per spec (Owner / PR / Plaintiff / Defendant / Contact-1 / Contact-2)
const FALLBACK_PHONES = (lead) =>
  cleanList([lead.phone_one, lead.phone_two, lead.phone_three, lead.phone_four, lead.phone_five]);
const FALLBACK_EMAILS = (lead) =>
  cleanList([lead.email_one, lead.email_two, lead.email_three]);

function probateTabs(lead) {
  return [
    {
      key: "owner",
      label: "Owner",
      name: joinName(lead.first_name, lead.last_name, lead.middle_name),
      phones: cleanList([
        lead.owner_phone_1, lead.owner_phone_2, lead.owner_phone_3,
        ...FALLBACK_PHONES(lead),
      ]),
      emails: cleanList([
        lead.owner_email_1, lead.owner_email_2, lead.owner_email_3,
        ...FALLBACK_EMAILS(lead),
      ]),
    },
    {
      key: "pr",
      label: "Personal Representative",
      shortLabel: "PR",
      name: joinName(lead.pr_first_name, lead.pr_last_name, lead.pr_middle_name),
      address: addressFromPrefix(lead, "pr"),
      phones: cleanList([
        lead.pr_phone_1, lead.pr_phone_2, lead.pr_phone_3,
        ...FALLBACK_PHONES(lead),
      ]),
      emails: cleanList([
        lead.pr_email_1, lead.pr_email_2, lead.pr_email_3,
        ...FALLBACK_EMAILS(lead),
      ]),
    },
    {
      key: "relative1",
      label: "Relative 1",
      name: joinName(lead.relative1_first_name, lead.relative1_last_name, lead.relative1_middle_name),
      phones: cleanList([lead.relative1_phone_1, lead.relative1_phone_2, lead.relative1_phone_3]),
      emails: cleanList([lead.relative1_email_1, lead.relative1_email_2, lead.relative1_email_3]),
    },
    {
      key: "relative2",
      label: "Relative 2",
      name: joinName(lead.relative2_first_name, lead.relative2_last_name, lead.relative2_middle_name),
      phones: cleanList([lead.relative2_phone_1, lead.relative2_phone_2, lead.relative2_phone_3]),
      emails: cleanList([lead.relative2_email_1, lead.relative2_email_2, lead.relative2_email_3]),
    },
  ];
}

function divorceTabs(lead) {
  return [
    {
      key: "plaintiff",
      label: "Plaintiff",
      name: joinName(lead.plaintiff_first_name, lead.plaintiff_last_name, lead.plaintiff_middle_name),
      phones: cleanList([
        lead.plaintiff_phone_1, lead.plaintiff_phone_2, lead.plaintiff_phone_3,
        ...FALLBACK_PHONES(lead),
      ]),
      emails: cleanList([
        lead.plaintiff_email_1, lead.plaintiff_email_2, lead.plaintiff_email_3,
        ...FALLBACK_EMAILS(lead),
      ]),
    },
    {
      key: "defendant",
      label: "Defendant",
      name: joinName(lead.defendant_first_name, lead.defendant_last_name, lead.defendant_middle_name),
      phones: cleanList([
        lead.defendant_phone_1, lead.defendant_phone_2, lead.defendant_phone_3,
        ...FALLBACK_PHONES(lead),
      ]),
      emails: cleanList([
        lead.defendant_email_1, lead.defendant_email_2, lead.defendant_email_3,
        ...FALLBACK_EMAILS(lead),
      ]),
    },
    {
      key: "other_contact1",
      label: "Other Contact 1",
      name: lead.other_contact1_name,
      phones: cleanList([lead.other_contact1_phone_1, lead.other_contact1_phone_2, lead.other_contact1_phone_3]),
      emails: cleanList([lead.other_contact1_email_1, lead.other_contact1_email_2, lead.other_contact1_email_3]),
    },
    {
      key: "other_contact2",
      label: "Other Contact 2",
      name: lead.other_contact2_name,
      phones: cleanList([lead.other_contact2_phone_1, lead.other_contact2_phone_2, lead.other_contact2_phone_3]),
      emails: cleanList([lead.other_contact2_email_1, lead.other_contact2_email_2, lead.other_contact2_email_3]),
    },
  ];
}

function genericTabs(lead) {
  // FSBO, EXPIRED, CANCELED, WITHDRAWN, NOD, TAX_LIENS — per spec.
  return [
    {
      key: "contact1",
      label: "Contact 1",
      name: lead.contact1_name,
      phones: cleanList([
        lead.contact1_phone_1, lead.contact1_phone_2, lead.contact1_phone_3,
        ...FALLBACK_PHONES(lead),
      ]),
      emails: cleanList([
        lead.contact1_email_1, lead.contact1_email_2, lead.contact1_email_3,
        ...FALLBACK_EMAILS(lead),
      ]),
    },
    {
      key: "contact2",
      label: "Contact 2",
      name: lead.contact2_name,
      phones: cleanList([
        lead.contact2_phone_1, lead.contact2_phone_2, lead.contact2_phone_3,
        ...FALLBACK_PHONES(lead),
      ]),
      emails: cleanList([
        lead.contact2_email_1, lead.contact2_email_2, lead.contact2_email_3,
        ...FALLBACK_EMAILS(lead),
      ]),
    },
    {
      key: "list_agent",
      label: "Listing Agent",
      name: lead.list_agent_name,
      phones: cleanList([lead.list_agent_phone]),
      emails: cleanList([lead.list_agent_email]),
    },
  ];
}

// Strips tabs that contain no name/phones/emails/address, returns ordered list.
function pruneTabs(tabs) {
  return tabs.filter((t) =>
    (t.name && String(t.name).trim()) ||
    (t.phones && t.phones.length > 0) ||
    (t.emails && t.emails.length > 0) ||
    (t.address && String(t.address).trim())
  );
}

// Top-level dispatcher. Returns ordered, non-empty tabs for the given lead.
export function buildPartiesTabs(lead) {
  if (!lead) return [];
  const src = String(lead.source_model || lead.document_type || lead.status || "").toUpperCase();
  let tabs;
  if (src.includes("PROBATE")) tabs = probateTabs(lead);
  else if (src.includes("DIVORCE")) tabs = divorceTabs(lead);
  else tabs = genericTabs(lead);
  return pruneTabs(tabs);
}

// Field keys hidden from the "Additional details" dump because they are surfaced
// in the Parties tabs or other derived sections of the page.
export const PARTY_SHOWN_KEYS = [
  // Address / location
  "property_address", "address", "mailing_address",
  "property_city", "city", "mailing_city",
  "state", "property_state", "mailing_state",
  "property_zip", "zip", "mailing_zip",
  "county_name", "county",
  // Financial
  "price", "list_price", "amount_due", "balance_due",
  // Property
  "bedrooms", "beds", "bathrooms", "baths", "sqft", "square_feet", "year_built",
  "property_use", "property_type", "institution_name", "lender_name",
  "case_number", "docket_number",
  // Generic phone / email fallbacks
  "phone_one", "phone_two", "phone_three", "phone_four", "phone_five",
  "email_one", "email_two", "email_three",
  // Owner
  "first_name", "last_name", "middle_name", "full_name", "owner_1_name", "owner_2_name", "property_owned",
  "owner_phone_1", "owner_phone_2", "owner_phone_3",
  "owner_email_1", "owner_email_2", "owner_email_3",
  // Personal Representative
  "pr_first_name", "pr_last_name", "pr_middle_name",
  "pr_address", "pr_city", "pr_state", "pr_zip",
  "pr_phone_1", "pr_phone_2", "pr_phone_3",
  "pr_email_1", "pr_email_2", "pr_email_3",
  // Plaintiff / Defendant
  "plaintiff_first_name", "plaintiff_last_name", "plaintiff_middle_name",
  "plaintiff_phone_1", "plaintiff_phone_2", "plaintiff_phone_3",
  "plaintiff_email_1", "plaintiff_email_2", "plaintiff_email_3",
  "defendant_first_name", "defendant_last_name", "defendant_middle_name",
  "defendant_phone_1", "defendant_phone_2", "defendant_phone_3",
  "defendant_email_1", "defendant_email_2", "defendant_email_3",
  // Other / Contact 1 / Contact 2
  "other_contact1_name", "other_contact1_phone_1", "other_contact1_phone_2", "other_contact1_phone_3",
  "other_contact1_email_1", "other_contact1_email_2", "other_contact1_email_3",
  "other_contact2_name", "other_contact2_phone_1", "other_contact2_phone_2", "other_contact2_phone_3",
  "other_contact2_email_1", "other_contact2_email_2", "other_contact2_email_3",
  "contact1_name", "contact1_phone_1", "contact1_phone_2", "contact1_phone_3",
  "contact1_email_1", "contact1_email_2", "contact1_email_3",
  "contact2_name", "contact2_phone_1", "contact2_phone_2", "contact2_phone_3",
  "contact2_email_1", "contact2_email_2", "contact2_email_3",
  // Listing Agent
  "list_agent_name", "list_agent_phone", "list_agent_email",
  // Misc legacy fields surfaced elsewhere
  "owner_address", "owner_city", "owner_state", "owner_zip",
  "file_date", "date", "processed_date", "date_created", "status",
  "google_map_image_url",
];

// Hide any relative_N_* / relativeN_* keys — they are rendered in party tabs.
export const RELATIVE_RE = /^relative_?\d+(_|$)/i;
